import { useCallback, useEffect, useReducer, useState } from 'react';

import { AuthResponse } from 'api';
import { Overlay } from 'components';
import { IconButton } from 'components/ui';
import { X } from 'assets/vector';
import { useAppContext } from 'context';
import { gtag, renderMessage } from 'utils';
import PhoneInput from './PhoneInput';
import CodeConfirm from './CodeConfirm';
import Error from './Error';
import Success from './Success';

import styles from './SignIn.module.scss';

type Props = {
  onClose: () => void;
};

export type State = {
  status: 'phone' | 'code' | 'done';
  response?: AuthResponse;
  token?: string;
  error?: string | null;
};

export type Action =
  | { type: 'RESPONSE'; payload: AuthResponse }
  | { type: 'ERROR'; payload?: string | null }
  | { type: 'DONE'; payload: string };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'RESPONSE':
      return { status: 'code', response: action.payload };

    case 'DONE':
      return { status: 'done', token: action.payload };

    case 'ERROR':
      return { ...state, error: action.payload };

    default:
      return state;
  }
};

const Content = ({ close }: { close: (token?: string) => void }) => {
  const [state, dispatch] = useReducer(reducer, { status: 'phone' });

  const renderContent = () => {
    const { status, error } = state;

    const header = (
      <div className={styles.message}>
        {renderMessage(error || `play.auth.${status}.message`)}
      </div>
    );

    let body;

    if (error) {
      body = (
        <Error
          buttonLabel={`play.auth.${status}.error.button`}
          onClose={() => {
            gtag('event', `error_${status}_cliked`);
            dispatch({ type: 'ERROR', payload: null });
          }}
        />
      );
    } else {
      switch (status) {
        case 'phone':
          body = <PhoneInput dispatch={dispatch} />;
          break;

        case 'code':
          body = <CodeConfirm state={state} dispatch={dispatch} />;
          break;

        case 'done':
          body = (
            <Success
              onClose={() => {
                gtag('event', 'done_cliked');
                close(state.token);
              }}
            />
          );
          break;

        default:
          body = null;
          break;
      }
    }

    return (
      <>
        {header}
        {body}
      </>
    );
  };

  return (
    <div className={styles.signin}>
      <div className={styles.header}>
        <IconButton icon={X} onClick={() => close(state.token)} />
      </div>

      {renderContent()}
    </div>
  );
};

const Signin = ({ onClose: closeCallback }: Props) => {
  const [visible, setVisible] = useState(true);
  const [token, setToken] = useState<string>();

  const { auth } = useAppContext();

  useEffect(() => {
    if (visible) {
      gtag('event', 'phone_register_open');
    }
  }, [visible]);

  const apply = useCallback((token?: string) => {
    setToken(token);
    setVisible(false);
  }, []);

  const onClose = useCallback(() => {
    if (token) {
      auth.setToken(token);
    }

    closeCallback();
  }, [auth, closeCallback, token]);

  return (
    <Overlay visible={visible} onClose={onClose} ratio={1.5}>
      <Content close={apply} />
    </Overlay>
  );
};

export default Signin;
